// COPIED FILE - do not edit here.
// Master: _shared/ at the top level. Change it there, then run
// `node sync-shared.mjs`, or services will silently disagree about
// permissions and token formats.

/**
 * SERVER-ONLY — imported as "@py/shared/server".
 * jsonwebtoken needs Node.js and must never reach the browser bundle;
 * that is why this file is NOT exported from the package's main entry.
 */
import jwt from "jsonwebtoken";
import { fail } from "./errors";
import type { TokenUser } from "./types";

/**
 * Guard #1 — is the pass (JWT) real and unexpired?
 * Every service builds its own guard from the shared JWT secret.
 */
export function makeRequireAuth(secret: string) {
  return (req: any, res: any, next: any) => {
    const header: string | undefined = req.headers.authorization;
    const token = header?.startsWith("Bearer ") ? header.slice(7) : null;
    if (!token) return fail(res, 401, "UNAUTHENTICATED", "Sign in to continue.");
    try {
      req.user = jwt.verify(token, secret) as TokenUser;
      next();
    } catch {
      return fail(res, 401, "TOKEN_EXPIRED", "Your session has expired. Please sign in again.");
    }
  };
}

/**
 * Express 4 does not catch errors thrown inside async routes — an uncaught
 * one crashes the whole service. Wrap every async route with this so errors
 * reach the service's error handler instead.
 */
export const wrap =
  (fn: (req: any, res: any, next: any) => any) => (req: any, res: any, next: any) => {
    Promise.resolve(fn(req, res, next)).catch(next);
  };

/** Guard #2 — does your role allow this action? */
export function requirePermission(permission: string) {
  return (req: any, res: any, next: any) => {
    const user: TokenUser | undefined = req.user;
    if (!user?.permissions?.includes(permission)) {
      return fail(res, 403, "FORBIDDEN", "You don't have permission to do that.");
    }
    next();
  };
}

export type { TokenUser };
